import { styled } from "@mui/system";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { SanitisedTradeSummaryRes } from "src/store/features/api/generated";
import { ITradeInput } from "src/store/features/swap.slice";
import { COUNTRY_CODES } from "src/utils/constants";
import { IRefLogic } from "./useTradeRefLogic";

interface ErrorWrapperProps {
  hide: boolean;
}
export const ErrorWrapper = styled("div")<ErrorWrapperProps>(({ hide }) => ({
  color: "#FC4C4C",
  fontSize: "14px",
  visibility: hide ? "hidden" : "visible",
}));

export const ErrorHereLink = styled(RouterLink)(() => ({
  color: "#FEFCF8",
  fontFamily: "Roobert",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  "&:hover": {
    opacity: 0.7,
  },
}));

interface useTradeErrorsProps {
  sellInput: ITradeInput;
  buyInput: ITradeInput;
  ref: IRefLogic;
  handleSubmitButtonChange: (change: Record<string, any>) => void;
}
const useTradeErrors = (props: useTradeErrorsProps) => {
  const { sellInput, buyInput, handleSubmitButtonChange } = props;
  const { previousSellInputRef, previousBuyInputRef } = props.ref;

  const [error, setError] = React.useState<React.ReactNode>(
    <ErrorWrapper hide={true}></ErrorWrapper>,
  );

  const [hasError, setHasError] = React.useState<boolean>(false);

  const changeErrorMessage = React.useCallback(
    (type: string): void => {
      switch (type) {
        case "insufficientFunds":
          const hereLink = COUNTRY_CODES.includes(sellInput.code)
            ? "/wallets/details"
            : "/wallets/deposit";
          const Here = <ErrorHereLink to={hereLink}>{sellInput.code} here</ErrorHereLink>;
          setError(
            <ErrorWrapper hide={false}>
              Please deposit additional {Here} to execute your trade.
            </ErrorWrapper>,
          );
          handleSubmitButtonChange({ color: "salmon", text: "Insufficient Funds", disabled: true });
          setHasError(true);
          break;
        case "duplicateCode":
          setError(<ErrorWrapper hide={false}>Can not trade the same coin</ErrorWrapper>);
          handleSubmitButtonChange({ color: "salmon", text: "Duplicate Currency", disabled: true });
          setHasError(true);
          break;
        case "belowMinTrade":
          setError(<ErrorWrapper hide={false}>Below minimum transaction</ErrorWrapper>);
          handleSubmitButtonChange({
            color: "salmon",
            text: "Below Minimum Order",
            disabled: true,
          });
          setHasError(true);
          break;
        case "reset":
          setError(<ErrorWrapper hide={true}>x</ErrorWrapper>);
          setHasError(false);
          handleSubmitButtonChange({ color: "salmon", text: "Place Order", disabled: false });
          break;
        default:
          setError(<ErrorWrapper hide={true}>x</ErrorWrapper>);
          handleSubmitButtonChange({ color: "salmon", text: "Place Order", disabled: false });
          setHasError(false);
          break;
      }
    },
    [sellInput],
  );

  const handleErrorFromSummaryResponse = (summary: SanitisedTradeSummaryRes): boolean => {
    if (!summary.error) {
      changeErrorMessage("reset");
      return false;
    }

    handleCustomError(summary.error);
    return true;
  };

  const handleCustomError = (error: string): void => {
    setError(<ErrorWrapper hide={false}>{error}</ErrorWrapper>);
    setHasError(true);
    handleSubmitButtonChange((prev: any) => ({ ...prev, disabled: true }));
  };

  React.useEffect((): void => {
    const previousSellValue = previousSellInputRef.current.value;
    const previousBuyValue = previousBuyInputRef.current.value;
    const previousSellCode = previousSellInputRef.current.code;
    const previousBuyCode = previousBuyInputRef.current.code;

    // Priority: duplicateCode > insufficientFunds > reset
    if (
      sellInput.code === buyInput.code &&
      sellInput.code !== "Select" &&
      buyInput.code !== "Select"
    ) {
      changeErrorMessage("duplicateCode");
    } else if (
      Number(sellInput.value) > Number(sellInput.maxTotal) &&
      sellInput.code !== "Select"
    ) {
      changeErrorMessage("insufficientFunds");
    } else if (
      sellInput.value !== previousSellValue ||
      buyInput.value !== previousBuyValue ||
      sellInput.code !== previousSellCode ||
      buyInput.code !== previousBuyCode
    ) {
      changeErrorMessage("reset");
    }

    /**
     * If Statement here is for a specific case where the user changes sell currency while they have an error for 'insufficientFunds'.
     * This is to reset the error message when the user changes the sell currency. Allowing for a trade summary to be calucated.
     */
    if (sellInput.code !== "Select" && sellInput.code !== previousSellInputRef.current.code) {
      changeErrorMessage("reset");
    }

    // Update refs
    previousSellInputRef.current.value = sellInput.value;
    previousBuyInputRef.current.value = buyInput.value;
    previousSellInputRef.current.code = sellInput.code;
    previousBuyInputRef.current.code = buyInput.code;
  }, [sellInput, buyInput]);

  return { error, hasError, changeErrorMessage, handleCustomError, handleErrorFromSummaryResponse };
};

export default useTradeErrors;
